import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, ZoomIn, ZoomOut, ExternalLink } from 'lucide-react';

/**
 * Visualizador de imagem estilo WhatsApp: clica e a imagem "salta" num overlay
 * escuro em tela cheia. Fechar com X, clique no fundo ou ESC. Zoom e download.
 *
 * Uso:
 *   const [aberta, setAberta] = useState<string | null>(null);
 *   <img onClick={() => setAberta(url)} ... />
 *   <ImageLightbox src={aberta} alt="Print" onClose={() => setAberta(null)} />
 *
 * `src` null/undefined = fechado. Reutilizável em qualquer card (convergência,
 * WhatsApp, anexos…).
 */
export function ImageLightbox({
  src,
  alt = 'Imagem',
  onClose,
}: {
  src: string | null | undefined;
  alt?: string;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);

  // Reseta o zoom sempre que abre uma nova imagem
  useEffect(() => { setZoom(1); }, [src]);

  // ESC fecha; trava o scroll do body enquanto aberto
  useEffect(() => {
    if (!src) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [src, onClose]);

  const stop = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

  if (!src) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      {/* Barra de ações */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5" onClick={stop}>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))}
          className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          title="Diminuir zoom"
        >
          <ZoomOut className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))}
          className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          title="Aumentar zoom"
        >
          <ZoomIn className="h-4 w-4" />
        </button>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          title="Abrir em nova aba"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
        <a
          href={src}
          download
          className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          title="Baixar"
        >
          <Download className="h-4 w-4" />
        </a>
        <button
          type="button"
          onClick={onClose}
          className="h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          title="Fechar (ESC)"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Imagem */}
      <img
        src={src}
        alt={alt}
        onClick={stop}
        style={{ transform: `scale(${zoom})` }}
        className="max-h-[90vh] max-w-[92vw] object-contain rounded-md shadow-2xl transition-transform duration-150 cursor-default select-none"
        draggable={false}
      />
    </div>,
    document.body,
  );
}

export default ImageLightbox;
